import { observer } from "mobx-react-lite";
import {
  Calendar,
  Clock,
  Copy,
  Trash2,
  Edit,
  Eye,
  MoreVertical,
} from "lucide-react";
import { useTran } from "@/hooks";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { getFrameworkColor } from "../../../utils/framework.helpers";
import Progress from "@/components/common/Progress";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { useESGReportListStore } from "../../../hooks/useESGReportListStore";
import { PATHS } from "@/lib/router";
import { frameworkInfos } from "@/lib/constants/frameworks";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

dayjs.extend(relativeTime);

const ESGReportCard = observer(({ report }) => {
  const { t } = useTran();
  const navigate = useNavigate();
  const { duplicateReport, handleOpenDeleteDialog } = useESGReportListStore();
  const {
    _id,
    reportName,
    reportYear,
    framework,
    orgName,
    status,
    currentStep,
    completedCount,
    createdAt,
    updatedAt,
    progressPercentage,
    selectedDisclosures,
    delegatedDisclosures,
  } = report || {};

  const badgeClasses = "rounded-md py-1 px-2";

  const statusLabel = {
    completed: "Completed",
    in_progress: "In Progress",
  };
  const statusColor =
    progressPercentage === 100 ? "bg-nxstart-green" : "bg-blue-600";
  const frameworkColor = getFrameworkColor(framework);
  const frameworkInfo =
    frameworkInfos.find((info) => info.key === framework) || null;
  const frameworkIcon = frameworkInfo?.image || null;

  const handleEdit = () => {
    const path =
      PATHS.ESG_REPORT.replace(":framework", framework).replace(
        ":stepId",
        currentStep,
      ) + `?reportId=${_id}`;
    navigate(path);
  };

  return (
    <div className="p-4 flex flex-col justify-between gap-y-6 bg-card rounded-radius border-border border shadow-sm">
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-x-2">
            {frameworkIcon ? (
              <img
                src={frameworkIcon}
                alt={`${framework} logo`}
                className="h-10 w-10 rounded-radius border border-border p-1 object-contain"
              />
            ) : null}
            <h3 className="flex-1 text-foreground">{reportName}</h3>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {progressPercentage === 100 ? (
                <DropdownMenuItem onClick={handleEdit}>
                  <Edit />
                  {t("Edit")}
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem
                onClick={() => duplicateReport(_id, `${reportName} (Copy)`)}
              >
                <Copy />
                {t("Duplicate")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                variant="destructive"
                onClick={() => handleOpenDeleteDialog(report)}
              >
                <Trash2 />
                {t("Delete")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge
            variant="outline"
            className={`${badgeClasses} ${frameworkColor}`}
          >
            {framework}
          </Badge>
          <Badge
            variant="outline"
            className={`${badgeClasses} border-nxstart-green/50 text-nxstart-green`}
          >
            {orgName}
          </Badge>
          <Badge variant="outline" className={badgeClasses}>
            <Calendar size={14} />
            {reportYear}
          </Badge>
          <Badge className={`${badgeClasses} ${statusColor} text-white`}>
            <Clock size={14} />
            {t(statusLabel[status])}
          </Badge>
        </div>
      </div>

      <div className="space-y-3">
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{t("Progress")}</span>
            <span>{progressPercentage}%</span>
          </div>
          <Progress value={progressPercentage} />
          <div className="flex items-center text-xs text-muted-foreground gap-x-1">
            <span>
              {completedCount}/
              {selectedDisclosures?.length + delegatedDisclosures?.length}
            </span>
            <span>{t("disclosures completed")}</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-500">
          <div className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            <span>Created {dayjs(createdAt).format("MMM D, YYYY")}</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            <span>Updated {dayjs(updatedAt).fromNow()}</span>
          </div>
        </div>

        <Separator orientation="horizontal" />

        {progressPercentage === 100 ? (
          <Button variant="secondary" onClick={handleEdit} className="w-full">
            <Eye />
            {t("View Report")}
          </Button>
        ) : (
          <Button onClick={handleEdit} className="w-full">
            <Edit />
            {t("Continue Editing")}
          </Button>
        )}
      </div>
    </div>
  );
});

export default ESGReportCard;
